/*
 * Nhóm dịch vụ chia số (họp 30/10): 3 nhóm chính Nội khoa, Ngoại khoa, Cấy tóc (New Hair).
 * Data vào nhóm nào thì chỉ chia cho telesale thuộc nhóm đó — tuần tự trong nhóm, có thể ưu tiên người đang thiếu số theo tỉ lệ yêu cầu.
 * Cài đặt đồng bộ với server qua settingsApi, localStorage chỉ là bản đệm.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { mangOf, sourceOf, type LeadLocal, type Mang } from './logic';

export type GroupKey = 'noi' | 'ngoai' | 'toc';
export interface Group { key: GroupKey; label: string; mangs: Mang[]; color: string }
export const GROUPS: Group[] = [
  { key: 'noi', label: 'Nội khoa', mangs: ['Clinic', 'Partnership'], color: 'cyan' },
  { key: 'ngoai', label: 'Ngoại khoa', mangs: ['PTTM'], color: 'magenta' },
  { key: 'toc', label: 'Cấy tóc', mangs: ['Newhair'], color: 'gold' },
];
export const groupByKey = (k?: GroupKey) => GROUPS.find((g) => g.key === k);
/** Mảng hiện có của data → nhóm (Clinic = Nội khoa, PTTM = Ngoại khoa, Newhair = Cấy tóc) */
export const groupOfMang = (m?: Mang): Group => GROUPS.find((g) => m && g.mangs.includes(m)) ?? GROUPS[0];

export interface GroupCfg {
  /** Bật chia theo nhóm: chia tự động / chia lại chỉ giao cho người cùng nhóm */
  enabled: boolean;
  /** Dịch vụ (từ khoá trong dịch vụ quan tâm, ghi chú) thuộc từng nhóm — gõ "Ultherapy" thì tự hiểu Nội khoa */
  svc: Record<GroupKey, string[]>;
  /** Telesale (mã nhân viên dạng chuỗi) → các nhóm nhận số */
  staff: Record<string, GroupKey[]>;
  /** Tỉ lệ yêu cầu (%) trong nhóm — dùng cho bảng cân bằng số và ưu tiên bù số */
  ratio: Record<string, number>;
  /** Chia ưu tiên người đang thiếu số so với tỉ lệ yêu cầu (bù số) */
  compensate: boolean;
}

export const DEFAULT_GROUP_CFG: GroupCfg = {
  enabled: true,
  svc: {
    noi: ['ultherapy', 'thermage', 'hifu', 'laser', 'nám', 'mụn', 'sẹo', 'tiêm', 'filler', 'botox', 'triệt lông', 'peel', 'căng chỉ', 'trẻ hoá', 'trẻ hóa', 'da', 'giảm cân', 'clinic'],
    ngoai: ['nâng mũi', 'mũi', 'cắt mí', 'mí', 'ngực', 'hút mỡ', 'cấy mỡ', 'phẫu thuật', 'hàm', 'pttm', 'phun xăm'],
    toc: ['cấy tóc', 'tóc', 'newhair', 'new hair', 'hói', 'rụng tóc', 'da đầu'],
  },
  staff: {},
  ratio: {},
  compensate: true,
};

interface GroupStore { cfg: GroupCfg; setCfg: (p: Partial<GroupCfg>) => void; reset: () => void }
export const useGroupCfg = create<GroupStore>()(persist((set) => ({
  cfg: DEFAULT_GROUP_CFG,
  setCfg: (p) => set((s) => ({ cfg: { ...s.cfg, ...p } })),
  reset: () => set({ cfg: DEFAULT_GROUP_CFG }),
}), { name: 'khothi-tele-groups-v1', version: 1 }));

const fold = (s: string) => s.normalize('NFC').toLowerCase();

/** Từ khoá khớp nguyên từ: "da" không khớp trong "danh", "mí" không khớp trong "mít" */
const hasWord = (t: string, k: string) =>
  new RegExp(`(^|[^\\p{L}\\p{N}])${fold(k).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}\\p{N}])`, 'u').test(t);

/** Nhóm theo chữ (dịch vụ quan tâm, ghi chú): từ khoá dài khớp trước để "da đầu" ra Cấy tóc chứ không ra Nội khoa */
export function groupOfText(text: string, cfg: GroupCfg): GroupKey | undefined {
  const t = fold(text);
  const hits = GROUPS.flatMap((g) => (cfg.svc[g.key] ?? []).filter((k) => k.trim() && hasWord(t, k.trim())).map((k) => ({ g: g.key, n: k.length })));
  return hits.sort((a, b) => b.n - a.n)[0]?.g;
}

/** Nhóm của một data: nhóm chọn tay → dịch vụ quan tâm / ghi chú → mảng (trang nguồn); không đoán được thì undefined */
export function groupOfLead(r: ChannelCustomerItem, l: LeadLocal | undefined, cfg: GroupCfg): GroupKey | undefined {
  if (l?.group) return l.group;
  const text = [l?.needs?.dich_vu, l?.needs?.van_de, r.note, sourceOf(r)].filter(Boolean).join(' ');
  const byText = text ? groupOfText(text, cfg) : undefined;
  if (byText) return byText;
  const m = mangOf(r, l);
  return m ? groupOfMang(m).key : undefined;
}

/** Telesale nhận được nhóm này không — người chưa gắn nhóm nhận khách tràn của mọi nhóm (như "chưa phân luồng" ở Dashboard) */
export const staffInGroup = (cfg: GroupCfg, staffId: string | number, g?: GroupKey) => {
  if (!cfg.enabled || !g) return true;
  const gs = cfg.staff[String(staffId)];
  return !gs?.length || gs.includes(g);
};
